import React from 'react';
import { Edit, FileText, DollarSign, Calendar, User, Tag, AlertCircle, MessageSquare, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { ContactOpportunity } from '@/hooks/useContactDetails';
import { useCurrency } from '@/hooks/useCurrency';
import { format } from 'date-fns';
import { formatDateInAccountTz, formatDateTimeInAccountTz, formatTimeInAccountTz } from '@/lib/accountTimezone'
import { useTranslation } from "react-i18next";

interface OpportunityCardProps {
  opportunity: ContactOpportunity;
  onEdit: (opportunity: ContactOpportunity) => void;
}

const statusConfig = {
  open: {
    label: 'Aberta',
    borderColor: 'border-[#2563EB]/30 dark:border-blue-500/40',
    bgColor: 'bg-[#EEF4FF] dark:bg-blue-500/5',
    badgeClass: 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE] dark:bg-blue-500/20 dark:text-[#60A5FA] dark:border-blue-500/30',
  },
  won: {
    label: 'Ganha',
    borderColor: 'border-[#22C55E]/40',
    bgColor: 'bg-green-50 dark:bg-green-500/5',
    badgeClass: 'bg-green-100 text-[#16A34A] border-green-200 dark:bg-green-500/20 dark:text-[#22C55E] dark:border-green-500/30',
  },
  lost: {
    label: 'Perdida',
    borderColor: 'border-[#EF4444]/40',
    bgColor: 'bg-red-50 dark:bg-red-500/5',
    badgeClass: 'bg-red-100 text-[#EF4444] border-red-200 dark:bg-red-500/20 dark:text-[#EF4444] dark:border-red-500/30',
  },
};

export const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, onEdit }) => {
  const { t } = useTranslation();
  const { formatCurrency } = useCurrency();
  const status = opportunity.status || 'open';
  const config = statusConfig[status];

  const formatDate = (date: string | null | undefined) => {
    if (!date) return null;
    try {
      return formatDateInAccountTz(date);
    } catch {
      return null;
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border ${config.borderColor} ${config.bgColor} hover:border-opacity-60 transition-colors duration-200`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Badge className={`${config.badgeClass} border text-xs px-2 py-0.5`}>
            {config.label}
          </Badge>
          <span className="text-sm font-medium text-[#0F172A] truncate dark:text-[#FAFAFA]">
            {opportunity.title || 'Sem titulo'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-[#F1F5F9] flex-shrink-0 dark:hover:bg-[#3F3F46]"
          onClick={() => onEdit(opportunity)}
        >
          <Edit className="w-3.5 h-3.5 text-[#64748B] dark:text-[#A1A1AA]" />
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Layers className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
          <span className="text-[#64748B] dark:text-[#A1A1AA]">
            {opportunity.pipeline_name} / {opportunity.stage_name}
          </span>
        </div>

        {opportunity.product && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
            <span className="text-[#334155] dark:text-[#D4D4D8]">{opportunity.product}</span>
          </div>
        )}

        {(opportunity.price !== undefined && opportunity.price > 0) && (
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
            <span className="text-[#334155] font-medium dark:text-[#D4D4D8]">
              {formatCurrency(opportunity.price)}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
          <span className="text-[#64748B] dark:text-[#A1A1AA]">
            {t("common:shared.createdOn", { date: formatDate(opportunity.created_at) || t("common:shared.unknownDate") })}
          </span>
        </div>

        {status === 'won' && (
          <>
            {opportunity.sale_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                <span className="text-[#334155] dark:text-[#D4D4D8]">
                  Venda em {formatDate(opportunity.sale_date)}
                </span>
              </div>
            )}
            {opportunity.won_by_user_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                <span className="text-[#334155] dark:text-[#D4D4D8]">
                  Vendido por {opportunity.won_by_user_name}
                </span>
              </div>
            )}
          </>
        )}

        {status === 'lost' && (
          <>
            {opportunity.closed_at && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-[#EF4444] flex-shrink-0" />
                <span className="text-[#334155] dark:text-[#D4D4D8]">
                  Perdida em {formatDate(opportunity.closed_at)}
                </span>
              </div>
            )}
            {opportunity.loss_reason_name && (
              <div className="flex items-center gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-[#EF4444] flex-shrink-0" />
                <span className="text-[#334155] dark:text-[#D4D4D8]">
                  Motivo: {opportunity.loss_reason_name}
                </span>
              </div>
            )}
          </>
        )}

        {opportunity.notes && (
          <div className="flex items-start gap-2 text-sm mt-2 pt-2 border-t border-[#E5E7EB] dark:border-[#3F3F46]/50">
            <MessageSquare className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5 dark:text-[#71717A]" />
            <span className="text-[#64748B] line-clamp-2 dark:text-[#A1A1AA]">{opportunity.notes}</span>
          </div>
        )}

        {opportunity.tags && opportunity.tags.length > 0 && (
          <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-[#E5E7EB] dark:border-[#3F3F46]/50">
            <Tag className="w-4 h-4 text-[#94A3B8] flex-shrink-0 dark:text-[#71717A]" />
            <div className="flex flex-wrap gap-1">
              {opportunity.tags.map((tag, idx) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className="text-xs px-1.5 py-0 border-0 bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpportunityCard;
